import {
  labelPoint, polygonPath, radarPoints, ringPoints, SCALE_MAX,
} from '@/domain/lifewheel/radar';
import type { ID } from '@/domain/types';

/** Radius des Netzes; der Rand laesst Platz fuer die Beschriftung. */
const R = 100;
/** Breit genug fuer seitliche Beschriftungen mit bis zu MAX_ZEICHEN Zeichen und Wert. */
const RAND = 80;
const RINGE = [2, 4, 6, 8, 10];
/** Laengere Namen passen nicht in den Rand - gekuerzt, voll im Tooltip. */
const MAX_ZEICHEN = 10;
const kurz = (name: string) => (name.length > MAX_ZEICHEN ? `${name.slice(0, MAX_ZEICHEN - 1).trimEnd()}…` : name);

export interface RadarAxis {
  id: ID;
  name: string;
  color: string;
  value: number;
}

interface RadarChartProps {
  axes: RadarAxis[];
  /** Fruehere Messung, blass dahinter. Werte in derselben Achsenreihenfolge. */
  compare?: number[];
  compareLabel?: string;
  /** Name fuer Screenreader - Standard „Lebensrad über n Bereiche“. */
  ariaLabel?: string;
}

export function RadarChart({ axes, compare, compareLabel, ariaLabel }: RadarChartProps) {
  const n = axes.length;
  const punkte = radarPoints(axes.map((a) => a.value), R);
  const vergleich = compare ? radarPoints(compare, R) : undefined;
  const seite = (R + RAND) * 2;

  return (
    <svg
      viewBox={`${-(R + RAND)} ${-(R + RAND)} ${seite} ${seite}`}
      className="h-auto w-full max-w-sm"
      role="img"
      aria-label={ariaLabel ?? `Lebensrad über ${n} Bereiche`}
    >
      {/* Gitter */}
      {RINGE.map((level) => (
        <path
          key={level}
          d={polygonPath(ringPoints(n, R, level))}
          fill="none"
          stroke="var(--color-line)"
          strokeWidth={level === SCALE_MAX ? 1.25 : 0.75}
        />
      ))}

      {/* Speichen */}
      {axes.map((axis, i) => {
        const aussen = ringPoints(n, R, SCALE_MAX)[i]!;
        return (
          <line
            key={axis.id}
            x1={0} y1={0} x2={aussen.x} y2={aussen.y}
            stroke="var(--color-line)"
            strokeWidth={0.75}
          />
        );
      })}

      {/* Fruehere Messung - gestrichelt, damit sie ohne Farbe unterscheidbar ist */}
      {vergleich && vergleich.length > 0 && (
        <path
          d={polygonPath(vergleich)}
          fill="var(--color-ink-faint)"
          fillOpacity={0.1}
          stroke="var(--color-ink-faint)"
          strokeWidth={1}
          strokeDasharray="3 3"
        >
          {compareLabel && <title>{compareLabel}</title>}
        </path>
      )}

      {/* Aktuelle Messung */}
      <path
        d={polygonPath(punkte)}
        fill="var(--color-accent)"
        fillOpacity={0.18}
        stroke="var(--color-accent)"
        strokeWidth={1.75}
        strokeLinejoin="round"
      />

      {/* Ecken in der Bereichsfarbe - der einzige Ort, an dem Farbe Sinn traegt */}
      {punkte.map((p, i) => (
        <circle key={axes[i]!.id} cx={p.x} cy={p.y} r={3} fill={axes[i]!.color} />
      ))}

      {/* Beschriftung */}
      {axes.map((axis, i) => {
        const l = labelPoint(i, n, R + 14);
        return (
          <text
            key={axis.id}
            x={l.x}
            y={l.y}
            textAnchor={l.anchor}
            dominantBaseline="middle"
            className="fill-ink-muted text-[9px]"
          >
            <title>{`${axis.name}: ${axis.value}`}</title>
            {kurz(axis.name)} {axis.value}
          </text>
        );
      })}
    </svg>
  );
}
